import React, { useState, useEffect } from 'react';
import { getAgents } from '../services/api';
import { 
  Card, 
  CardContent, 
  Typography, 
  Checkbox, 
  Button, 
  Box, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider, 
  Chip,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import { 
  Check as CheckIcon,
  ClearAll as ClearAllIcon,
  SelectAll as SelectAllIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { agentColors } from '../theme';

// Estilos personalizados
const AgentItem = styled(ListItem)(({ theme, selected, colorIndex }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  cursor: 'pointer',
  backgroundColor: selected ? `${agentColors[colorIndex]}22` : theme.palette.background.paper,
  '&:hover': {
    backgroundColor: selected ? `${agentColors[colorIndex]}33` : theme.palette.action.hover,
  },
  transition: 'all 0.2s',
  border: selected ? `1px solid ${agentColors[colorIndex]}` : '1px solid #e0e0e0',
  boxShadow: selected ? `0 2px 4px ${agentColors[colorIndex]}33` : 'none',
  padding: theme.spacing(1.2, 2),
  '& .MuiListItemIcon-root': {
    minWidth: '42px',
  },
  '& .MuiCheckbox-root': {
    '& svg': {
      width: '1.3rem',
      height: '1.3rem',
    }
  }
}));

// Removed ColorCircle component as we'll use the background color of the ListItem instead

const AgentSelector = ({ selectedAgents, onAgentsChange }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Sempre carregar agentes ao inicializar o componente
    // para garantir que temos a lista mais atualizada
    fetchAgents();
    
    // Também monitorar quando selectedAgents muda para verificar se precisamos atualizar a lista
    console.log("AgentSelector: selectedAgents changed:", selectedAgents?.length);
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("AgentSelector: Buscando agentes do backend");
      const response = await getAgents();
      
      if (response.success) {
        console.log("AgentSelector: Recebeu agentes:", response.agents);
        setAgents(response.agents);
        
        // Se não houver agentes selecionados, selecionar todos os disponíveis
        if (!selectedAgents || selectedAgents.length === 0) {
          const availableAgents = response.agents.filter(agent => agent.available);
          console.log("AgentSelector: Selecionando agentes disponíveis:", availableAgents.length);
          if (availableAgents.length > 0) {
            onAgentsChange(availableAgents);
          }
        }
      } else {
        setError('Falha ao carregar agentes');
      }
    } catch (err) {
      console.error("AgentSelector: Erro ao buscar agentes:", err);
      setError(`Erro: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAgentToggle = (agent) => {
    const isSelected = selectedAgents.some(a => a.id === agent.id);
    
    if (isSelected) {
      // Remover da seleção, mas garantir que pelo menos um agente esteja selecionado
      if (selectedAgents.length > 1) {
        onAgentsChange(selectedAgents.filter(a => a.id !== agent.id));
      }
    } else {
      // Adicionar à seleção
      onAgentsChange([...selectedAgents, agent]);
    }
  };

  const toggleAllAgents = (select) => {
    if (select) {
      // Selecionar todos os agentes disponíveis
      const availableAgents = agents.filter(agent => agent.available);
      onAgentsChange(availableAgents);
    } else {
      // Manter pelo menos um agente selecionado
      if (selectedAgents.length > 1) {
        onAgentsChange([selectedAgents[0]]);
      }
    }
  };

  if (loading) {
    return (
      <Card elevation={2}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <CircularProgress size={40} />
            <Typography variant="body1" ml={2}>
              Carregando agentes...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card elevation={2}>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  const availableAgents = agents.filter(agent => agent.available);

  return (
    <Card elevation={2}>
      <CardContent>
        <Box 
          sx={{
            backgroundColor: '#f8f9fa',
            borderRadius: 1,
            p: 2,
            mb: 2,
            borderLeft: '3px solid #3f51ff'
          }}
        >
          <Typography 
            variant="h6" 
            component="h3"
            sx={{
              fontSize: '1.2rem',
              fontWeight: 600,
              letterSpacing: '0.01em',
              color: '#2c3e50',
              mb: 1.5
            }}
          >
            Agentes para Otimização
          </Typography>
          
          <Stack direction="row" spacing={1.5}>
            <Button
              size="medium"
              variant="outlined"
              startIcon={<SelectAllIcon />}
              onClick={() => toggleAllAgents(true)}
              disabled={availableAgents.length === 0 || 
                      (selectedAgents.length === availableAgents.length)}
              sx={{ 
                fontWeight: 500,
                minWidth: '110px'
              }}
            >
              Todos
            </Button>
            <Button
              size="medium"
              variant="outlined"
              startIcon={<ClearAllIcon />}
              onClick={() => toggleAllAgents(false)}
              disabled={selectedAgents.length <= 1}
              color="secondary"
              sx={{ 
                fontWeight: 500,
                minWidth: '110px'
              }}
            >
              Limpar
            </Button>
          </Stack>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        {agents.length === 0 ? (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2,
              '& .MuiAlert-message': {
                fontSize: '1rem',
                fontWeight: 500
              }
            }}
          >
            Não existem agentes cadastrados. Para começar, vá para a seção "Agentes" e adicione pelo menos um agente.
          </Alert>
        ) : availableAgents.length === 0 ? (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 2,
              '& .MuiAlert-message': {
                fontSize: '1rem',
                fontWeight: 500
              }
            }}
          >
            Existem agentes cadastrados, mas todos estão marcados como indisponíveis. Vá para a seção "Agentes" e marque pelo menos um agente como disponível.
          </Alert>
        ) : (
          <List sx={{ mb: 2 }}>
            {availableAgents.map((agent, index) => {
              const isSelected = selectedAgents.some(a => a.id === agent.id);
              const colorIndex = index % agentColors.length;
              
              return (
                <AgentItem
                  key={agent.id}
                  selected={isSelected}
                  colorIndex={colorIndex}
                  onClick={() => handleAgentToggle(agent)}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={isSelected}
                      tabIndex={-1}
                      disableRipple
                      sx={{
                        color: agentColors[colorIndex],
                        '&.Mui-checked': {
                          color: agentColors[colorIndex],
                        },
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={agent.name}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: '1.05rem',
                        fontWeight: 700,
                        letterSpacing: '0.01em',
                        color: '#222',
                        textShadow: isSelected ? '0 0 0.5px rgba(0,0,0,0.2)' : 'none'
                      }
                    }}
                  />
                </AgentItem>
              );
            })}
          </List>
        )}
        
        <Chip 
          label={`${selectedAgents.length} de ${availableAgents.length} agentes selecionados`}
          size="medium"
          color={availableAgents.length > 0 ? "primary" : "warning"}
          variant="outlined"
          sx={{
            fontSize: '0.9rem',
            height: '32px',
            '& .MuiChip-label': {
              padding: '0 12px',
              fontWeight: 500
            }
          }}
        />
      </CardContent>
    </Card>
  );
};

export default AgentSelector;